import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Stack,
  Button,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import AccountTree from '@mui/icons-material/AccountTree';
import { Link as RouterLink } from 'react-router-dom';
import SEO from '../components/SEO';

function Neo4jContacts() {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  const sections = [
    {
      title: t('neo4j_page.sections.introduction.title'),
      content: t('neo4j_page.sections.introduction.content')
    },
    {
      title: t('neo4j_page.sections.context.title'),
      content: t('neo4j_page.sections.context.content'),
      items: t('neo4j_page.sections.context.items', { returnObjects: true })
    },
    {
      title: t('neo4j_page.sections.modeling.title'),
      content: t('neo4j_page.sections.modeling.content'),
      items: t('neo4j_page.sections.modeling.items', { returnObjects: true })
    },
    {
      title: t('neo4j_page.sections.data_generation.title'),
      content: t('neo4j_page.sections.data_generation.content')
    },
    {
      title: t('neo4j_page.sections.implementation.title'),
      content: t('neo4j_page.sections.implementation.content'),
      features: t('neo4j_page.sections.implementation.features', { returnObjects: true })
    },
    {
      title: t('neo4j_page.sections.android.title'),
      content: t('neo4j_page.sections.android.content'),
      features: t('neo4j_page.sections.android.features', { returnObjects: true })
    },
  ];

  return (
    <>
      <SEO 
        title={`Neo4j Contacts - ${t('hero.name')}`}
        description={t('neo4j_page.header.description')}
        type="article"
      />
      <Box sx={{ pb: 10, position: 'relative', overflowX: 'hidden', width: '100%' }}>
        {/* Header Background */}
        <Box
        sx={{
          pt: 12,
          pb: 8,
          width: '100%',
          backgroundColor: '#0e1626',
          backgroundImage: 'linear-gradient(135deg, rgba(255, 215, 61, 0.15) 0%, rgba(14, 22, 38, 0.9) 100%)',
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid #1f2a3d',
          overflowX: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
            <Button
              component={RouterLink}
              to="/"
              startIcon={<ArrowBack />}
              sx={{ color: '#fff' }}
            >
              {t('neo4j_page.back')}
            </Button>
          </Stack>

          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              mb: 2,
              color: '#fff',
            }}
          >
            {t('neo4j_page.title')}
          </Typography>

          <Typography
            variant="h5"
            sx={{
              color: '#a3a3a3',
              mb: 3,
              fontWeight: 400,
            }}
          >
            {t('neo4j_page.subtitle')}
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            <Chip 
              label="Neo4j" 
              sx={{ 
                bgcolor: 'rgba(255, 215, 61, 0.2)', 
                color: '#ffd93d', 
                borderRadius: '8px',
                fontWeight: 600,
              }} 
            />
            <Chip 
              label="Cypher" 
              sx={{ 
                bgcolor: 'rgba(78, 205, 196, 0.2)', 
                color: '#4ecdc4', 
                borderRadius: '8px',
                fontWeight: 600,
              }} 
            />
            <Chip 
              label="Android" 
              sx={{ 
                bgcolor: 'rgba(255, 107, 107, 0.2)', 
                color: '#ff6b6b', 
                borderRadius: '8px',
                fontWeight: 600,
              }} 
            />
            <Chip 
              label="Java" 
              sx={{ 
                bgcolor: 'rgba(78, 205, 196, 0.2)', 
                color: '#4ecdc4', 
                borderRadius: '8px',
                fontWeight: 600,
              }} 
            />
            <Chip 
              label="NoSQL" 
              sx={{ 
                bgcolor: 'rgba(255, 215, 61, 0.2)', 
                color: '#ffd93d', 
                borderRadius: '8px',
                fontWeight: 600,
              }} 
            />
          </Stack>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ mt: 8 }}>
        {sections.map((section, idx) => (
          <Box key={idx} sx={{ mb: 6 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 3,
                color: '#fff',
              }}
            >
              {section.title}
            </Typography>
            
            {section.content && (
              <Typography
                variant="body1"
                sx={{
                  color: '#d4d4d4',
                  lineHeight: 1.8,
                  mb: section.items || section.features ? 3 : 0,
                }}
              >
                {section.content}
              </Typography>
            )}

            {section.items && (
              <Box component="ul" sx={{ pl: 3, color: '#d4d4d4' }}>
                {section.items.map((item, itemIdx) => (
                  <li key={itemIdx} style={{ marginBottom: '12px', lineHeight: 1.8 }}>
                    <Typography variant="body1" component="span">
                      {item}
                    </Typography>
                  </li>
                ))}
              </Box>
            )}

            {section.features && (
              <Grid container spacing={2}>
                {section.features.map((feature, featureIdx) => (
                  <Grid item xs={12} sm={6} md={4} key={featureIdx}>
                    <Box
                      sx={{
                        p: 2.5,
                        backgroundColor: '#141414',
                        border: '2px solid #2a2a2a',
                        borderRadius: '12px',
                        height: '100%',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          borderColor: featureIdx % 3 === 0 ? '#ff6b6b' : featureIdx % 3 === 1 ? '#4ecdc4' : '#ffd93d',
                          boxShadow: `0 8px 24px ${featureIdx % 3 === 0 ? 'rgba(255, 107, 107, 0.2)' : featureIdx % 3 === 1 ? 'rgba(78, 205, 196, 0.2)' : 'rgba(255, 215, 61, 0.2)'}`,
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#a3a3a3',
                          lineHeight: 1.7,
                        }}
                      >
                        {feature}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        ))}

        {/* Strengths & Limitations */}
        <Box sx={{ mt: 8, mb: 6 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 3,
              color: '#fff',
            }}
          >
            {t('neo4j_page.analysis.title')}
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 4,
                  backgroundColor: '#141414',
                  border: '2px solid #2a2a2a',
                  borderRadius: '12px',
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '6px',
                    background: '#4ecdc4',
                    zIndex: 1,
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: '#4ecdc4',
                  }}
                >
                  {t('neo4j_page.analysis.strengths.title')}
                </Typography>
                <Box component="ul" sx={{ pl: 3, color: '#d4d4d4', m: 0 }}>
                  {t('neo4j_page.analysis.strengths.items', { returnObjects: true }).map((item, idx) => (
                    <li key={idx} style={{ marginBottom: '8px', lineHeight: 1.7 }}>
                      <Typography variant="body2" component="span">
                        {item}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 4,
                  backgroundColor: '#141414',
                  border: '2px solid #2a2a2a',
                  borderRadius: '12px',
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '6px',
                    background: '#ff6b6b',
                    zIndex: 1,
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: '#ff6b6b',
                  }}
                >
                  {t('neo4j_page.analysis.limitations.title')}
                </Typography>
                <Box component="ul" sx={{ pl: 3, color: '#d4d4d4', m: 0 }}>
                  {t('neo4j_page.analysis.limitations.items', { returnObjects: true }).map((item, idx) => (
                    <li key={idx} style={{ marginBottom: '8px', lineHeight: 1.7 }}>
                      <Typography variant="body2" component="span">
                        {item}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Conclusion */}
        <Box
          sx={{
            mt: 8,
            p: 4,
            backgroundColor: '#141414',
            border: '2px solid #2a2a2a',
            borderRadius: '12px',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '6px',
              background: 'linear-gradient(90deg, #ff6b6b, #4ecdc4, #ffd93d)',
              zIndex: 1,
            },
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <AccountTree sx={{ fontSize: 32, color: '#ffd93d' }} />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#fff',
              }}
            >
              {t('neo4j_page.conclusion.title')}
            </Typography>
          </Stack>
          <Typography
            variant="body1"
            sx={{
              color: '#d4d4d4',
              lineHeight: 1.8,
            }}
          >
            {t('neo4j_page.conclusion.content')}
          </Typography>
        </Box>
      </Container>
    </Box>
    </>
  );
}

export default Neo4jContacts;

